/**
 * Transcript Display Component
 * shows transcript in full or segmented view
 */

import React, { useEffect, useRef } from 'react';
import Text from '@amzn/meridian/text';
import Icon from '@amzn/meridian/icon';
import chatTokens from '@amzn/meridian-tokens/base/icon/chat';
import { useAppDispatch, useAppSelector } from '../../../store/store';
import { selectMergedSegments } from '../../../store/transcriptSlice';
import { TranscriptLine, toRollingLines } from './rollingTranscript';
import { showSuccess } from '../../../store/toastsSlice';
import { formatUnixTimestampToDateTime } from '../../../utils/dateUtil';
import {
  FONT_SIZE_XSMALL,
  LINE_HEIGHT_BODY,
  RECORDING_TEXT_COLOR,
  RECORDING_TEXT_FONT_STYLE,
  TEXT_COLOR_PRIMARY,
  TEXT_COLOR_SECONDARY,
} from '../cssConstants';
import { useFontSize } from './FontSizeContext';
import {
  conversationsWithItemsByTranscriptId,
  conversationsToDisplay,
  itemsToItemsByTranscriptId,
  speakerToTone,
  transcriptIdToTextTone,
  transcriptsToConversations,
} from './dataTransformations';
import type {
  SpeakerTone,
  TranscriptTextTone,
} from './dataTransformations';
import styles from './TranscriptDisplay.module.scss';
import ConversationSeparatorComponent from './ConversationSeparator';

type ViewMode = 'paragraph' | 'chat';
export type TranslationDisplayMode = 'en' | 'es' | 'both';

export interface TranscriptDisplayProps {
  viewMode?: ViewMode;
  translationDisplayMode?: TranslationDisplayMode;
  maxLines?: number;
}

interface RollingSegmentComponentProps {
  lines: TranscriptLine[];
}

const speakerToneClassNames: Record<SpeakerTone, string> = {
  speakerA: styles.speakerA,
  speakerB: styles.speakerB,
  speakerC: styles.speakerC,
  speakerD: styles.speakerD,
  secondary: styles.secondaryText,
};

const transcriptTextToneClassNames: Record<TranscriptTextTone, string> = {
  primary: styles.primaryText,
  secondary: styles.secondaryText,
};

const transcriptTextToneColors: Record<TranscriptTextTone, string> = {
  primary: TEXT_COLOR_PRIMARY,
  secondary: TEXT_COLOR_SECONDARY,
};

/**
 * Empty state shown when there is no transcript content.
 */
const EmptyStateComponent = () => (
  <div className={styles.emptyState}>
    <div>
      <div className={styles.emptyStateIcon}>
        <Icon tokens={chatTokens} />
      </div>
      <Text color="secondary">
        Full conversation will appear here...
      </Text>
    </div>
  </div>
);

/** Renders the transcript lines produced by `toRollingLines`. */
const RollingSegmentComponent: React.FC<RollingSegmentComponentProps> = ({ lines }) => (
  <div>
    {lines.map(line => (
      <div
        key={line.key}
        style={{
          marginBottom: '8px',
          lineHeight: LINE_HEIGHT_BODY,
          color: line.live ? RECORDING_TEXT_COLOR : TEXT_COLOR_PRIMARY,
          fontStyle: line.live ? RECORDING_TEXT_FONT_STYLE : undefined,
        }}
      >
        {line.text}
      </div>
    ))}
  </div>
);

const TranscriptDisplay: React.FC<TranscriptDisplayProps> = ({
  viewMode = 'paragraph',
  translationDisplayMode = 'both',
  maxLines,
}) => {
  const dispatch = useAppDispatch();
  const scrollRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const { fontSize } = useFontSize();

  const { currentSession } = useAppSelector((state) => state.session);
  const { accumulativeText, currentPartialText, transcripts, segments } = useAppSelector((state) => state.transcript);
  const {
    activeTranscriptId,
    isRecording,
    isFileStreaming,
  } = useAppSelector((state) => state.recording);
  const currentUserAlias = useAppSelector((state) => state.user.alias);
  const mergedSegments = useAppSelector(selectMergedSegments);

  // Auto-scroll should follow any active transcription source — live mic recording
  // or audio-file streaming — since both stream new segments into the transcript.
  const isActivelyTranscribing = isRecording || isFileStreaming;

  /**
   * Check if a timestamp (in milliseconds) is older than 10 minutes
   */
  const isOlderThan10Minutes = (timestampMs?: number): boolean => {
    if (!timestampMs) return false;
    const now = Date.now();
    const tenMinutesInMs = 10 * 60 * 1000;
    return (now - timestampMs) > tenMinutesInMs;
  };

  /**
   * Check if there are any recent (< 10 min) transcripts being postprocessed
   * This stops polling for transcripts older than 10 minutes (likely stuck/failed)
   */
  const hasRecentPostprocessing = React.useMemo(() => {
    return transcripts.some(t => {
      const created_at_ms = t.created_at ? new Date(t.created_at).getTime() : undefined;
      return t.status !== 'completed' && !isOlderThan10Minutes(created_at_ms);
    });
  }, [transcripts]);

  // Track previous postprocessing state to detect completion
  const prevPostprocessingRef = useRef(false);


  // Show toast when transcript enhancing completes (transitions from true → false)
  // Guard: transcripts.length > 0 prevents false trigger when resetTranscript() clears the array
  useEffect(() => {
    if (prevPostprocessingRef.current && !hasRecentPostprocessing && transcripts.length > 0) {
      dispatch(showSuccess('Transcript enhanced'));
    }
    prevPostprocessingRef.current = hasRecentPostprocessing;
  }, [hasRecentPostprocessing, transcripts.length, dispatch]);

  const scrollToBottom = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  };

  // Pin the transcript to the bottom while actively transcribing, regardless of
  // view/translation mode or source (mic recording or audio-file streaming).
  // A ResizeObserver on the content catches every height change — new segments, live
  // partial text, async translations growing a segment, font-size and mode switches —
  // which a dependency-array effect alone misses because those mutations land after paint.
  useEffect(() => {
    if (!isActivelyTranscribing) return;

    scrollToBottom();

    const content = contentRef.current;
    if (!content || typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(() => {
      scrollToBottom();
    });
    observer.observe(content);

    return () => observer.disconnect();
  }, [isActivelyTranscribing]);

  /**
   * Split text into sentences for line-by-line display
   * Handles . ! ? as sentence endings, respects minimum word count (5 words)
   */
  const splitIntoSentences = (text: string): string[] => {
    if (!text) return [];

    const MIN_WORDS = 5; // Must match backend MIN_SENTENCE_WORDS
    const sentences: string[] = [];
    let currentSentence = '';

    for (let i = 0; i < text.length; i++) {
      currentSentence += text[i];

      // Check if this is a sentence-ending punctuation
      if ('.!?'.includes(text[i])) {
        // Look ahead to check if next char is space or end of string
        const nextChar = text[i + 1];
        if (!nextChar || nextChar === ' ') {
          // Count words in current sentence
          const wordCount = currentSentence.trim().split(/\s+/).filter(w => w.length > 0).length;

          // Only split if we have enough words
          if (wordCount >= MIN_WORDS) {
            sentences.push(currentSentence.trim());
            currentSentence = '';
          }
          // Otherwise, keep accumulating (don't split short phrases like "Yes." "OK.")
        }
      }
    }

    // Add remaining text if any
    if (currentSentence.trim()) {
      sentences.push(currentSentence.trim());
    }

    return sentences;
  };

  const getFullText = () => {
    // Always show session transcript (existing) + live WebSocket data (new recording)
    const existingText = currentSession?.full_text || '';
    const liveText = accumulativeText + (currentPartialText ? ' ' + currentPartialText : '');

    // Combine: existing + new (with separator if both exist)
    if (existingText && liveText) {
      return existingText + ' ' + liveText;
    }
    return existingText || liveText;
  };

  const fullText = getFullText();
  // In bilingual mode, segments may exist even if fullText hasn't been assembled yet
  const hasBilingualSegments = segments.some(s => !s.isPartial && s.detectedLanguage);
  const hasContent = Boolean(fullText) || hasBilingualSegments || Boolean(currentPartialText);
  const confirmedSegments = segments.filter(s => !s.isPartial);
  const itemsToConversations = transcriptsToConversations(transcripts);
  const transcriptConversations = itemsToConversations(confirmedSegments);
  const mergedItemsByTranscriptId = itemsToItemsByTranscriptId(mergedSegments);
  const activeConversationTranscriptId = isActivelyTranscribing
    ? activeTranscriptId
    : null;
  const activeConversationCreator = isActivelyTranscribing
    ? currentUserAlias
    : null;
  const conversationsForDisplay = conversationsToDisplay(
    activeConversationTranscriptId,
  )(
    activeConversationCreator,
  );
  const transcriptConversationDisplays = conversationsForDisplay(
    transcriptConversations,
  );
  const mergedConversationDisplays = conversationsWithItemsByTranscriptId(
    mergedItemsByTranscriptId,
  )(transcriptConversationDisplays);

  // Detect bilingual mode from session (language_code contains comma = bilingual)
  const isBilingualMode = currentSession?.language_code?.includes(',') ?? false;

  /**
   * Determine the actual language of original text.
   * If translations contain "en" key → original is Spanish (or non-English).
   * If translations contain "es" key → original is English.
   * Falls back to detectedLanguage from Transcribe.
   */
  const getOriginalLangFromSegment = (seg: typeof segments[0]): string => {
    if (seg.translations) {
      // If we translated TO English, the original must be non-English (Spanish)
      if (seg.translations['en']) return 'es';
      // If we translated TO Spanish, the original must be English
      if (seg.translations['es']) return 'en';
    }
    // Fall back to detected language
    if (seg.detectedLanguage) return seg.detectedLanguage.substring(0, 2);
    return 'en';
  };

  /**
   * Get the translation text for a given target language from segment translations.
   */
  const getTranslationForLang = (seg: typeof segments[0], targetLang: string): string | undefined => {
    if (!seg.translations) return undefined;
    return seg.translations[targetLang];
  };

  /**
   * Plain segment sub-component for bilingual view.
   * Renders a segment's text with consistent styling (no labels or translations).
   */
  const PlainSegment = ({ seg, idx, textColor }: { seg: typeof segments[0]; idx: number; textColor: string }) => (
    <div
      key={seg.segmentId || `bilingual-${idx}`}
      style={{
        marginBottom: '8px',
        padding: '4px 12px',
        lineHeight: LINE_HEIGHT_BODY,
        color: textColor,
      }}
    >
      {seg.text}
    </div>
  );

  /**
   * Render a single bilingual segment based on translationDisplayMode.
   *
   * Logic:
   * - "en" mode: English-origin segments show just text. Spanish-origin show Original + Translation.
   * - "es" mode: Spanish-origin segments show just text. English-origin show Original + Translation.
   * - "both" mode: All segments show Original + Translation.
   */
  const renderBilingualSegment = (seg: typeof segments[0], idx: number) => {
    const textTone = transcriptIdToTextTone(seg.transcript_id);
    const textColor = transcriptTextToneColors[textTone];
    const originalLang = getOriginalLangFromSegment(seg);
    const translations = seg.translations;

    // Determine if this segment's original language matches the selected display mode.
    // If it matches, show just the plain text (no labels).
    const showPlainText = translationDisplayMode !== 'both' && originalLang === translationDisplayMode;

    if (showPlainText) {
      // Simple display — just the original text, no labels or translations
      return <PlainSegment seg={seg} idx={idx} textColor={textColor} />;
    }

    // Show Original + Translation with labels
    // Determine translation text based on mode
    let translationText: string | undefined;
    if (translationDisplayMode === 'en') {
      // User wants English — show English translation for Spanish-origin segments
      translationText = getTranslationForLang(seg, 'en');
    } else if (translationDisplayMode === 'es') {
      // User wants Spanish — show Spanish translation for English-origin segments
      translationText = getTranslationForLang(seg, 'es');
    } else {
      // "both" mode — show whatever translation exists
      translationText = translations ? Object.values(translations)[0] : undefined;
    }

    // If in single-language mode and no translation is available yet,
    // show just the original text plain (no "Original:" label clutter)
    if (translationDisplayMode !== 'both' && !translationText) {
      return <PlainSegment seg={seg} idx={idx} textColor={textColor} />;
    }

    const translationLangLabel = translationDisplayMode === 'both'
      ? (originalLang === 'en' ? 'ES' : 'EN')
      : translationDisplayMode.toUpperCase();

    return (
      <div
        key={seg.segmentId || `bilingual-${idx}`}
        style={{
          marginBottom: '12px',
          padding: '8px 12px',
          borderLeft: '3px solid #e0e0e0',
          borderRadius: '2px',
        }}
      >
        {/* Original text with label */}
        <div style={{ lineHeight: LINE_HEIGHT_BODY, color: textColor, marginBottom: '4px' }}>
          <span style={{ fontSize: FONT_SIZE_XSMALL, fontWeight: 'bold', marginRight: '6px', color: TEXT_COLOR_SECONDARY }}>
            Original:
          </span>
          {seg.text}
        </div>

        {/* Translation with label */}
        {translationText ? (
          <div
            style={{
              lineHeight: LINE_HEIGHT_BODY,
              color: TEXT_COLOR_SECONDARY,
              fontSize: `calc(${fontSize} - 1px)`,
              fontStyle: 'italic',
            }}
          >
            <span style={{ fontSize: FONT_SIZE_XSMALL, fontWeight: 'bold', marginRight: '6px' }}>
              Translation:
            </span>
            {translationText}
          </div>
        ) : !seg.isPartial && seg.detectedLanguage ? (
          <div style={{
            lineHeight: LINE_HEIGHT_BODY,
            color: TEXT_COLOR_SECONDARY,
            fontSize: FONT_SIZE_XSMALL,
            fontStyle: 'italic',
          }}>
            Translating...
          </div>
        ) : null}
      </div>
    );
  };

  /**
   * Bilingual transcript view — shows each segment with original + translation.
   * In chat mode, groups consecutive segments by speaker and shows speaker labels on change.
   * In paragraph mode, shows segments without speaker labels.
   */
  const BilingualTranscriptView = () => {
    if (confirmedSegments.length === 0 && !currentPartialText) {
      return <div />;
    }

    return (
      <div>
        {transcriptConversationDisplays.map((conversation) => (
          <React.Fragment key={conversation.transcriptId}>
            <ConversationSeparatorComponent
              title={conversation.separatorTitle}
            />
            {conversation.items.map((seg, idx) => {
              const prevSpeaker = idx > 0 ? conversation.items[idx - 1].speaker : null;
              const showSpeaker = viewMode === 'chat' && seg.speaker && seg.speaker !== prevSpeaker;
              const speakerClassName = speakerToneClassNames[
                speakerToTone(seg.speaker)
              ];
              const speakerPositionClassName = idx > 0
                ? styles.subsequentBilingualSpeaker
                : styles.firstBilingualSpeaker;

              return (
                <React.Fragment key={seg.segmentId || `${conversation.transcriptId}-${idx}`}>
                  {showSpeaker && (
                    <div className={`${styles.bilingualSpeaker} ${speakerPositionClassName}`}>
                      <span className={`${styles.speakerText} ${speakerClassName}`}>
                        [{seg.speaker}]
                      </span>
                    </div>
                  )}
                  {renderBilingualSegment(seg, idx)}
                </React.Fragment>
              );
            })}
          </React.Fragment>
        ))}

        {/* Show live partial text with detected language indicator */}
        {currentPartialText && (
          <div style={{
            marginBottom: '12px',
            padding: '8px 12px',
            borderLeft: '3px solid #cce5ff',
            borderRadius: '2px',
          }}>
            <div style={{
              color: RECORDING_TEXT_COLOR,
              fontStyle: RECORDING_TEXT_FONT_STYLE,
              lineHeight: LINE_HEIGHT_BODY
            }}>
              {currentPartialText}
            </div>
          </div>
        )}
      </div>
    );
  };

  /**
   * Paragraph transcript view — displays text split into sentences with per-transcript coloring.
   */
  const ParagraphTranscriptView = () => {
    if (confirmedSegments.length === 0) {
      // Fallback to session full_text if no segments
      const existingText = currentSession?.full_text || '';
      const sentences = splitIntoSentences(existingText);

      return (
        <div>
          {sentences.map((sentence, idx) => (
            <div key={idx} style={{ marginBottom: '8px', lineHeight: LINE_HEIGHT_BODY, color: TEXT_COLOR_PRIMARY }}>
              {sentence}
            </div>
          ))}
          {/* Show live partial text even when no confirmed segments yet */}
          {currentPartialText && (
            <div style={{
              color: RECORDING_TEXT_COLOR,
              fontStyle: RECORDING_TEXT_FONT_STYLE,
              marginBottom: '8px',
              lineHeight: LINE_HEIGHT_BODY
            }}>
              {currentPartialText}
            </div>
          )}
        </div>
      );
    }

    // Render segments grouped by transcript with appropriate colors
    return (
      <div>
        {transcriptConversationDisplays.map((conversation) => {
          const textTone = transcriptIdToTextTone(
            conversation.transcriptId === 'unknown'
              ? undefined
              : conversation.transcriptId,
          );
          const transcriptText = conversation.items.map(s => s.text).join(' ');
          const sentences = splitIntoSentences(transcriptText);
          const textColorClassName = transcriptTextToneClassNames[textTone];

          return (
            <React.Fragment key={conversation.transcriptId}>
              <ConversationSeparatorComponent
                title={conversation.separatorTitle}
              />
              {sentences.map((sentence, idx) => (
                <div
                  key={`${conversation.transcriptId}-${idx}`}
                  className={`${styles.transcriptSentence} ${textColorClassName}`}
                >
                  {sentence}
                </div>
              ))}
            </React.Fragment>
          );
        })}

        {/* Show live partial text on new line with grey italic styling */}
        {currentPartialText && (
          <div style={{
            color: RECORDING_TEXT_COLOR,
            fontStyle: RECORDING_TEXT_FONT_STYLE,
            marginBottom: '8px',
            lineHeight: LINE_HEIGHT_BODY
          }}>
            {currentPartialText}
          </div>
        )}
      </div>
    );
  };

  /**
   * Chat/segmented transcript view — displays merged segments with speaker labels.
   */
  const ChatTranscriptView = () => {
    if (mergedSegments.length === 0) {
      return (
        <div>
          <div style={{ textAlign: 'center', padding: '20px', color: TEXT_COLOR_SECONDARY }}>
            No segments available. Start a conversation to see transcript segments.
          </div>
        </div>
      );
    }

    return (
      <div>
        {mergedConversationDisplays.map((conversation) => (
          <React.Fragment key={conversation.transcriptId}>
            <ConversationSeparatorComponent
              title={conversation.separatorTitle}
            />
            {conversation.items.map((segment, idx) => {
              const speakerClassName = speakerToneClassNames[
                speakerToTone(segment.speaker)
              ];
              const textColorClassName = transcriptTextToneClassNames[
                transcriptIdToTextTone(segment.transcript_id)
              ];

              return (
                <div
                  key={`${conversation.transcriptId}-${idx}`}
                  className={styles.transcriptLine}
                >
                  <span
                    className={`${styles.speakerText} ${speakerClassName}`}
                  >
                    [{segment.speaker || 'Unknown'}]
                  </span>
                  <span className={styles.timestamp}>
                    [{formatUnixTimestampToDateTime(segment.timestamp)}]:
                  </span>
                  <span className={textColorClassName}>
                    {segment.text}
                  </span>
                </div>
              );
            })}
          </React.Fragment>
        ))}
      </div>
    );
  };

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Content Area */}
      <div
        ref={scrollRef}
        style={{
          flex: 1,
          minHeight: 0,
          padding: '12px',
          fontSize: fontSize,
          lineHeight: LINE_HEIGHT_BODY,
          // Consent mode (maxLines set) clips overflow; the full transcript scrolls.
          overflowY: maxLines != null ? 'hidden' : 'auto',
          overflowX: 'hidden',
          wordWrap: 'break-word',
          overflowWrap: 'break-word',
        }}
      >
      <div ref={contentRef} style={!hasContent ? { height: '100%' } : undefined}>
        {!hasContent ? (
          <EmptyStateComponent />
        ) : maxLines != null ? (
          <RollingSegmentComponent lines={toRollingLines(segments, currentPartialText, maxLines)} />
        ) : isBilingualMode ? (
          <BilingualTranscriptView />
        ) : viewMode === 'paragraph' ? (
          <ParagraphTranscriptView />
        ) : (
          <ChatTranscriptView />
        )}
      </div>
      </div>

    </div>
  );
};

export default TranscriptDisplay;
